"use client";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import InputPasswordWithReveal from "@/components/customs/InputPasswordWithReveal";
import { Checkbox } from "@/components/ui/checkbox";
import Link from "next/link";
import Image from "next/image";
import useLogin, { PhoneFormValues } from "./useLogin";
import getSelectedCountry from "@/lib/getSelectedCountry";
import { SearchParamsWrapper } from "@/components/customs/SearchParamsWrapper";
import { SubmitHandler, UseFormReturn } from "react-hook-form";
import { SupportedLanguage } from "@/lib/shared";
import lang from "./lang.json";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

function FormLoginWithPhoneContent({ locale }: { locale: SupportedLanguage }) {
  // Get the login hook with phone tab
  const {
    form,
    onSubmit: originalOnSubmit,
    isLoading,
  } = useLogin({ tab: "phone" });

  // Safely cast the form to the phone form type
  const typedForm = form as UseFormReturn<PhoneFormValues>;

  // Create a type-safe submit handler
  const handleSubmit: SubmitHandler<PhoneFormValues> = (data) => {
    // Since we know we're in the phone tab, we can safely call the original onSubmit
    return (originalOnSubmit as (values: PhoneFormValues) => Promise<void>)(
      data
    );
  };

  const countrySelected = getSelectedCountry(
    !!typedForm.watch("phone") ? (typedForm.watch("phone") as string) : ""
  );

  return (
    <Form {...typedForm}>
      <form
        onSubmit={typedForm.handleSubmit(handleSubmit)}
        className="space-y-4"
      >
        <FormField
          control={typedForm.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm text-gray-700">
                {lang["role"][locale]}
              </FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Personal" id="Personal" />
                      <Label htmlFor="Personal">
                        {lang["role-personal"][locale]}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Company" id="Company" />
                      <Label htmlFor="Company">
                        {lang["role-company"][locale]}
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={typedForm.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm text-gray-700">
                {lang["phone"][locale]}
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    {countrySelected?.image ? (
                      <span className="relative rounded-full overflow-hidden w-7 aspect-square">
                        <Image
                          src={countrySelected.image}
                          alt={countrySelected.name}
                          className="scale-200"
                          fill
                        />
                      </span>
                    ) : (
                      <span className="rounded-full overflow-hidden w-7 aspect-square bg-gray-300"></span>
                    )}
                  </span>
                  <Input
                    placeholder={
                      lang["enter-phone-number-start-with-plus"][locale]
                    }
                    className="pl-12"
                    {...field}
                    value={field.value as string}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <InputPasswordWithReveal
          form={typedForm}
          name="password"
          label={lang["password"][locale]}
          placeholder={lang["placeholder-password"][locale]}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Checkbox id="remember" />
            <label
              htmlFor="remember"
              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              {lang["remember-me"][locale]}
            </label>
          </div>

          <Link
            href={`/${locale}/auth/forgot-password?tab=phone`}
            className="text-sm text-primary hover:underline"
          >
            {lang["forgot-password"][locale]}
          </Link>
        </div>

        <Button
          rounded="full"
          disabled={isLoading}
          type="submit"
          variant="default"
          className="w-full py-6 h-12 mt-6"
        >
          {isLoading ? lang["logging-in"][locale] : lang["log-in"][locale]}
        </Button>
      </form>
    </Form>
  );
}

function FormLoginWithPhone({ locale }: { locale: SupportedLanguage }) {
  return (
    <SearchParamsWrapper>
      <FormLoginWithPhoneContent locale={locale} />
    </SearchParamsWrapper>
  );
}

export default FormLoginWithPhone;
